// Life in the UK — study guide content.
// Restructured from 42 overlapping sections into 12 learning modules.
// Previous version preserved at studyGuideData_pre_restructure_backup.js
//
// Card shape: { front, back, hook?, image?, imageAlt? }
//   back  - use \n to put each distinct fact on its own line. Long unbroken
//           sentences are hard to scan on a small card; one fact per line reads
//           far better and keeps the card inside its fixed height.
//   hook  - optional memory aid shown under the answer on the flipped card.
//           Only worth adding when it does real work: separates a commonly
//           confused pair, explains a term's origin, or states a causal link the
//           answer implies. It must NOT restate the answer, attach unrelated
//           trivia, or pad out an answer that is already only a few words.
//   image - optional asset path under public/, rendered beneath the answer.

export const sections = [
    {
        title: "🌍 1. The UK Today",
        cards: [
            {
                front: "Match the Capitals 🏛️",
                back: "England: London\nScotland: Edinburgh\nWales: Cardiff\nNI: Belfast",
            },
            {
                front: "Which UK nation has its own legal system?",
                back: "Scotland (Scots law) and Northern Ireland have separate legal systems from England & Wales.",
            },
            {
                front: "Largest city in Scotland 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Glasgow (approx 600k people)",
            },
            {
                front: "National Day of Wales 🏴󠁧󠁢󠁷󠁬󠁳󠁿",
                back: "St David's Day (March 1st)",
            },
            {
                front: "UK population (approx 2024)",
                back: "Around 67 million",
            },
            {
                front: "Largest Channel Island",
                back: "Jersey (population ~100k)",
            },
            {
                front: "Flag of St Patrick",
                back: "Diagonal red cross on a white background",
                hook: "Pat-Rick = Red. Patrick's cross is the red diagonal; Andrew's is the white one.",
                image: "/flags/st-patrick.svg",
                imageAlt: "Flag of St Patrick: red diagonal cross on white",
            },
            {
                front: "Flag of St Andrew",
                back: "Diagonal white cross on a blue background",
                image: "/flags/st-andrew.svg",
                imageAlt: "Flag of St Andrew: white diagonal cross on blue",
            },
            {
                front: "Highest mountain in the UK ⛰️",
                back: "Ben Nevis (located in Scotland)",
            },
            {
                front: "Location of the UK 🌍",
                back: "Sovereign country located off the northwest coast of mainland Europe",
            },
            {
                front: "What is Great Britain? 🇬🇧",
                back: "The island containing England, Scotland, and Wales (excludes Northern Ireland)",
                hook: "Great Britain is the island, so it stops at the water. Northern Ireland is on a different island, so it needs naming separately in \"UK\".",
            },
            {
                front: "Official name of the UK 🇬🇧",
                back: "\"The United Kingdom of Great Britain and Northern Ireland\"",
                hook: "The name spells out the maths: Great Britain (3 nations) + Northern Ireland = 4.",
            },
            {
                front: "UK Population 2021",
                back: "67.33 million.\nEngland ~84%, Scotland ~8%, Wales ~5%, NI ~3%.",
            },
            {
                front: "Population Growth History 📈",
                back: "1600: 4M\n1801: 8M\n1851: 20M\n1901: 40M\n1951: 50M\n2021: 67.33M",
            },
            {
                front: "Ageing Population 👴",
                back: "People living longer due to improved living standards & healthcare.\nRecord number aged 85+.\nImpacts pensions & healthcare costs.",
            },
            {
                front: "Ethnic Diversity 🌍",
                back: "UK is ethnically diverse, especially in large cities.\nMost common: White (European, Australian, Canadian, NZ, American).\nSignificant Asian, Black & mixed groups.",
            },
            {
                front: "Gender Equality in UK 👩‍💼",
                back: "Legal requirement: no discrimination by gender/marriage.\nEqual rights to work, own property, marry, divorce.\nWomen ~50% of workforce.",
            },
            {
                front: "Languages of the UK 🗣️",
                back: "English (main).\nWelsh (Wales).\nGaelic (parts of Scotland).\nIrish Gaelic (NI).\nMany accents & dialects.",
            },
            {
                front: "UK Currency 💷",
                back: "Pound sterling (£).\n100 pence = £1.\nNotes: £5, £10, £20, £50.\nCoins: 1p, 2p, 5p, 10p, 20p, 50p, £1, £2.",
            },
            {
                front: "Scotland & NI banknotes 🏦",
                back: "Have their own banknotes, valid everywhere but shops don't have to accept them.",
                hook: "Legal to offer, but a shop can still say no. \"Valid\" is not the same as \"must accept\".",
            },
        ],
    },
    {
        title: "🦴 2. Early Britain",
        cards: [
            {
                front: "Stone Age Britain 🪨",
                back: "First people were hunter-gatherers.\nBritain connected to Europe by land bridge.\nChannel formed ~10,000 years ago.",
            },
            {
                front: "First Farmers 🌾",
                back: "Arrived ~6,000 years ago from south-east Europe. Built houses, tombs and monuments.",
            },
            {
                front: "Stonehenge 🏛️",
                back: "Built in stages.\nProbably a special gathering place for seasonal ceremonies.\nLocated in Wiltshire.\nUNESCO World Heritage Site.",
            },
            {
                front: "Skara Brae 🏠",
                back: "Best preserved prehistoric village in northern Europe. Located on Orkney, off north coast of Scotland.",
            },
            {
                front: "Bronze Age Britain 🥉",
                back: "Began ~4,000 years ago.\nPeople lived in roundhouses.\nBuried dead in round barrows.\nSkilled metalworkers in bronze and gold.",
            },
            {
                front: "Iron Age Britain ⚔️",
                back: "People learned to make tools and weapons from iron.\nLived in roundhouses grouped into larger settlements.\nHill forts like Maiden Castle in Dorset.",
            },
            {
                front: "Iron Age Celts 🛡️",
                back: "Spoke Celtic language.\nMade first coins minted in Britain.\nSophisticated culture and economy.",
            },
            {
                front: "Maiden Castle 🏰",
                back: "Impressive Iron Age hill fort in Dorset, England.",
            },
            {
                front: "First British Coins 🪙",
                back: "Made by Iron Age people. Some inscribed with names of Iron Age kings.",
            },
            {
                front: "Britain's Land Bridge 🌍",
                back: "For much of the Stone Age, Britain was connected to the continent by a land bridge.",
            },
            {
                front: "Julius Caesar's Invasion (55 BC) ⚔️",
                back: "Led a Roman invasion of Britain.\nWas unsuccessful.\nBritain remained separate from Roman Empire for nearly 100 years.",
                hook: "Caesar came and failed; Claudius came and stayed.",
            },
            {
                front: "Emperor Claudius (AD 43) 👑",
                back: "Led the Roman army in a new invasion. Successfully occupied almost all of Britain.",
                hook: "Claudius = Conquered. Caesar (55 BC) tried first and failed.",
            },
            {
                front: "Boudicca 👸",
                back: "Queen of the Iceni tribe in eastern England.\nFought against the Romans.\nStatue on Westminster Bridge, London.",
            },
            {
                front: "Hadrian's Wall 🧱",
                back: "Built by Emperor Hadrian in north England to keep out the Picts (ancestors of Scottish people).\nIncludes forts Housesteads and Vindolanda.\nUNESCO World Heritage Site.",
                hook: "Near today's Scottish border.",
            },
            {
                front: "Roman Britain Duration ⏳",
                back: "Romans remained in Britain for 400 years. Left in AD 410 to defend other parts of the Roman Empire.",
                hook: "AD 43 to AD 410 — remember 400 years, not 500. The exam likes this one.",
            },
            {
                front: "Roman Legacy 🏗️",
                back: "Built roads, public buildings, created structure of law, introduced new plants and animals.",
            },
            {
                front: "First Christians in Britain ⛪",
                back: "First Christian communities began to appear in Britain during the 3rd and 4th centuries AD.",
            },
            {
                front: "Scotland and the Romans 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Areas of what is now Scotland were never conquered by the Romans.",
            },
            {
                front: "Anglo-Saxon Invasion 🛡️",
                back: "After Romans left (AD 410), Britain was invaded by Jutes, Angles and Saxons from northern Europe.\nTheir languages are basis of modern English.",
                hook: "\"Angles\" is where \"England\" comes from.",
            },
            {
                front: "Anglo-Saxon Kingdoms 👑",
                back: "By about AD 600, Anglo-Saxon kingdoms were established in Britain, mainly in what is now England.",
            },
            {
                front: "Sutton Hoo 💎",
                back: "Anglo-Saxon burial place of a king in modern Suffolk.\nBuried with treasure and armour in a ship covered by a mound of earth.",
            },
            {
                front: "Christian Missionaries ⛪",
                back: "St Patrick (patron saint of Ireland), St Columba (founded monastery on Iona), St Augustine (first Archbishop of Canterbury) spread Christianity.",
            },
            {
                front: "Viking Raids (AD 789) 🪓",
                back: "Vikings from Denmark and Norway first visited Britain to raid coastal towns, take goods and slaves.",
            },
            {
                front: "King Alfred the Great 👑",
                back: "United Anglo-Saxon kingdoms in England.\nDefeated the Vikings.\nPromoted learning and law.",
                hook: "The only English king called \"the Great\" — he beat the Vikings and pushed learning and law.",
            },
            {
                front: "The Danelaw 🗺️",
                back: "Area in east and north of England where Viking invaders settled.\nPlace names like Grimsby and Scunthorpe come from Viking languages.",
                hook: "Danelaw = where Danish law ran; -by and -thorpe names mark it.",
            },
            {
                front: "King Cnut (Canute) 👑",
                back: "Danish king who ruled England for a short period.",
            },
            {
                front: "Kenneth MacAlpin 👑",
                back: "United the people in the north against Viking threat.\nThe term 'Scotland' began to be used to describe that country.",
                hook: "MacAlpin made Scotland: he united the north and the name \"Scotland\" followed.",
            },
            {
                front: "Viking Conversion to Christianity ⛪",
                back: "Many Viking settlers converted to Christianity and mixed with local communities.",
            },
        ],
    },
    {
        title: "🏰 3. Norman Conquest & Middle Ages",
        cards: [
            {
                front: "Battle of Hastings (1066) ⚔️",
                back: "William, Duke of Normandy defeated Harold, Saxon king of England.\nHarold was killed.\nWilliam became king (William the Conqueror).",
                hook: "1066: the last successful invasion of England.",
            },
            {
                front: "Bayeux Tapestry 🧵",
                back: "Great piece of embroidery commemorating the Battle of Hastings. Can still be seen in France today.",
            },
            {
                front: "Domesday Book (1086) 📖",
                back: "Commissioned by William I.\nHe sent people to list all towns, villages, landowners, people and animals in England.\nStill exists today.",
                hook: "Twenty years after conquering (1066), William counts what he owns (1086).",
            },
            {
                front: "Norman French Influence 🗣️",
                back: "Language of the new ruling class.\nInfluenced development of English.\nWords like 'park' and 'beauty' come from Norman French.",
            },
            {
                front: "Feudalism System 👑",
                back: "King gave land to lords in return for military help.\nMost peasants were serfs – worked lord's land and could not move away.",
            },
            {
                front: "Statute of Rhuddlan (1284) 🏴󠁧󠁢󠁷󠁬󠁳󠁿",
                back: "King Edward I annexed Wales to the Crown of England. Castles built at Conwy and Caernarvon.",
            },
            {
                front: "Battle of Bannockburn (1314) ⚔️",
                back: "Robert the Bruce led Scottish forces to defeat the English army, securing Scottish independence.\nScotland remained unconquered.",
                hook: "Bruce at Bannockburn — both start with B, both mean Scotland stayed independent.",
            },
            {
                front: "Hundred Years War ⚔️",
                back: "England vs France.\nActually lasted 116 years.\nFamous battle: Agincourt (1415) where Henry V's outnumbered army defeated the French.",
                hook: "A \"Hundred\" Years War that actually ran 116 years. The trick question is the length.",
            },
            {
                front: "Black Death (1348) ☠️",
                back: "Probably a form of plague.\nKilled a third of England's population.\nCaused labour shortages, higher wages, end of serfdom.",
                hook: "Fewer workers meant survivors could demand more.",
            },
            {
                front: "Development of Parliament 🏛️",
                back: "Origins in king's council.\nMagna Carta (1215) established king was subject to law.\nTwo Houses developed: Lords and Commons.",
            },
            {
                front: "Geoffrey Chaucer ✍️",
                back: "Wrote The Canterbury Tales (poems about pilgrims). One of first books printed by William Caxton.",
            },
            {
                front: "Wars of the Roses (1455-1485) 🌹",
                back: "Civil war between House of Lancaster (red rose) and House of York (white rose).\nEnded at Battle of Bosworth Field.",
                hook: "LancasteR = Red rose. York = White. Ended 1485 at Bosworth, where the Tudors began.",
            },
            {
                front: "Henry VII 👑",
                back: "First Tudor king.\nWon at Bosworth Field (1485).\nMarried Elizabeth of York, uniting the two families.",
            },
            {
                front: "Magna Carta (1215) 📜",
                back: "King John forced to sign\nLimited King's power\nBasis of 'Rule of Law'",
                hook: "Magna Carta = \"Great Charter\". The one idea to keep: even the King is under the law.",
            },
            {
                front: "First Printing Press 🖨️",
                back: "William Caxton\nIntroduced to England\nin the 1470s",
            },
            {
                front: "The Habeas Corpus Act (1679) ⛓️",
                back: "Protected individuals from unlawful imprisonment",
            },
            {
                front: "The Bill of Rights (1689) 📜",
                back: "Limited monarch's power, established parliamentary authority",
            },
            {
                front: "Who was Wat Tyler?",
                back: "Leader of the Peasants' Revolt (1381), killed at Smithfield",
            },
            {
                front: "The Glorious Revolution (1688)",
                back: "Guaranteed the power of Parliament over the King",
            },
            {
                front: "The Middle Ages (1066-1485) 🛡️",
                back: "Period of constant war, Crusades, and the Hundred Years' War",
            },
            {
                front: "Laws in Wales Acts (1535-1542) 🏴󠁧󠁢󠁷󠁬󠁳󠁿",
                back: "Passed under Henry VIII – united England and Wales",
            },
            {
                front: "English in courts & documents ✍️",
                back: "Preferred language of courts and official documents by 1400",
            },
            {
                front: "The Roman Legacy 🏛️",
                back: "43 CE invasion\nLeft in 410 CE\nBuilt Hadrian's Wall",
            },
            {
                front: "The Vikings 🪓",
                back: "From Denmark/Norway\nSettled in 'Danelaw'\nDefeated by Alfred the Great",
            },
            {
                front: "1066: The Turning Point 👑",
                back: "Battle of Hastings\nWilliam the Conqueror (Norman)\nLast successful invasion",
            },
            {
                front: "The Peasants' Revolt (1381) 🌾",
                back: "Led by Wat Tyler\nProtest against poll tax\nBurned down Savoy Palace",
                hook: "Wat Tyler tackled the poll tax — and was killed at Smithfield.",
            },
            {
                front: "The Wars of the Roses 🌹",
                back: "Civil war: Lancaster (red rose) vs York (white rose)\nEnded at Bosworth (1485)",
            },
            {
                front: "Who was Alfred the Great?",
                back: "King of Wessex (871-899)\nDefeated Vikings, promoted education & law",
            },
            {
                front: "English Civil War (1642) ⚔️",
                back: "Cavaliers (King) vs Roundheads (Parliament)",
            },
            {
                front: "The Huguenots 🕊️",
                back: "Protestant refugees from France who came to Britain between 1680–1720",
            },
            {
                front: "Roman Britain: How many years?",
                back: "The Romans remained in Britain for 400 years (not 500)",
            },
            {
                front: "Scottish Clans: The Glencoe Massacre",
                back: "MacDonalds of Glencoe were massacred for being late to take an oath to King William",
            },
            {
                front: "The Gentry",
                back: "A new social class of landowners that appeared after the Black Death",
            },
            {
                front: "Last Welsh rebellion defeated",
                back: "By the mid-15th century",
            },
            {
                front: "St Columba & St Augustine ⛪",
                back: "Christian missionaries who led missions from Rome/Ireland to spread Christianity",
            },
        ],
    },
    {
        title: "👑 4. Tudors & Stuarts",
        cards: [
            {
                front: "Henry VII's Reign 👑",
                back: "Strengthened central administration, reduced power of nobles, built up monarchy's financial reserves.",
            },
            {
                front: "Henry VIII's Break from Rome ⛪",
                back: "Wanted to divorce Catherine of Aragon.\nPope refused.\nHenry established Church of England with king as head.",
            },
            {
                front: "The Reformation Europe 🌍",
                back: "Movement against Pope's authority.\nProtestants read Bible in own languages, believed personal relationship with God was key.",
            },
            {
                front: "Act for Government of Wales (1535-42) 🏴󠁧󠁢󠁷󠁬󠁳󠁿",
                back: "Wales formally united with England.\nWelsh sent MPs to House of Commons.\nWelsh legal system reformed.",
            },
            {
                front: "Edward VI 👑",
                back: "Strongly Protestant.\nBook of Common Prayer written.\nDied at age 15 after ruling just over 6 years.",
                hook: "Boy king, strongly Protestant, dead at 15 — the shortest reign of Henry's three children.",
            },
            {
                front: "Mary I (Bloody Mary) 🔥",
                back: "Devout Catholic who restored Catholicism.\nPersecuted Protestants (burned ~300).\nDied after a short reign.",
                hook: "\"Bloody\" for burning Protestants. Mary was the Catholic one.",
            },
            {
                front: "Elizabeth I's Religious Settlement ⛪",
                back: "Re-established Church of England.\nBalanced views of Catholics and extreme Protestants.\nAvoided serious religious conflict.",
            },
            {
                front: "Spanish Armada (1588) ⛵",
                back: "Spain sent large fleet to conquer England and restore Catholicism. English defeated the Armada.",
                hook: "Catholic Spain sails to undo Protestant England — and loses. 1588, under Elizabeth I.",
            },
            {
                front: "Mary, Queen of Scots 👑",
                back: "Catholic queen of Scotland.\nFled to England, imprisoned for 20 years by Elizabeth I.\nExecuted for plotting against Elizabeth.",
                hook: "Two Marys: Mary I burned Protestants; this one Elizabeth executed.",
            },
            {
                front: "Shakespeare's Contemporaries 🎭",
                back: "Elizabethan period known for rich poetry and drama.\nChristopher Marlowe and Ben Jonson were other famous playwrights.",
            },
            {
                front: "James VI and I 👑",
                back: "James VI of Scotland became James I of England (1603).\nUnited crowns but Scotland remained separate country.",
                hook: "Same man, two numbers: VI in Scotland, I in England. Crowns united in 1603, countries not yet.",
            },
            {
                front: "King James Bible 📖",
                back: "New translation of Bible into English.\nKnown as 'Authorised Version'.\nStill used in many Protestant churches today.",
            },
            {
                front: "Gunpowder Plot (1605) 💣",
                back: "Guy Fawkes and Catholic group tried to blow up King and Parliament.\nPlot exposed.\nCommemorated on 5 November.",
                hook: "\"Remember, remember the fifth of November\" — Guy Fawkes, 1605, still marked by Bonfire Night.",
            },
            {
                front: "Plantations of Ulster 🌾",
                back: "English government encouraged Scottish/English Protestants to settle in Ulster, taking land from Catholic landholders.",
            },
            {
                front: "Divine Right of Kings 👑",
                back: "James I and Charles I believed king was directly appointed by God. Led to conflict with Parliament.",
                hook: "If God appointed you, Parliament cannot overrule you — that belief is what led to civil war.",
            },
            {
                front: "English Civil War (1642-1651) ⚔️",
                back: "Cavaliers (king's supporters) vs Roundheads (Parliament supporters). Charles I executed in 1649.",
                hook: "Cavaliers = King (both have an \"a\" and long hair). Roundheads = Parliament, named for cropped hair.",
            },
            {
                front: "The Commonwealth (1649-1660) 🏛️",
                back: "England declared a republic.\nOliver Cromwell became Lord Protector.\nRuled until his death in 1658.",
            },
            {
                front: "The Restoration (1660) 👑",
                back: "After Oliver Cromwell's death, Parliament invited Charles II back from exile.\nCrowned King of England, Wales, Scotland and Ireland.",
                hook: "Cromwell dies, the monarchy is \"restored\" — Charles II comes back from exile in 1660.",
            },
            {
                front: "Great Plague (1665) ☠️",
                back: "Last major outbreak of bubonic plague in England.\nMajor outbreak in London; thousands died, especially in poorer areas.",
                hook: "Plague 1665, Fire 1666 — back-to-back disasters, plague first.",
            },
            {
                front: "Great Fire of London (1666) 🔥",
                back: "Destroyed much of the city including St Paul's Cathedral — around 13,000 houses.\nRebuilt by Sir Christopher Wren.",
                hook: "The year with three sixes. Wren rebuilt St Paul's afterwards.",
            },
            {
                front: "Habeas Corpus Act (1679) ⛓️",
                back: "Guaranteed no one could be held prisoner unlawfully. Every prisoner has right to court hearing.",
                hook: "Habeas corpus is Latin for \"produce the body\" — you cannot be held without being brought before a court.",
            },
            {
                front: "Royal Society 👨‍🔬",
                back: "Formed during Charles II's reign to promote 'natural knowledge'.\nOldest surviving scientific society.\nEarly members: Halley and Newton.",
            },
            {
                front: "Glorious Revolution (1688) 👑",
                back: "William of Orange invaded, James II fled.\nWilliam III and Mary II took throne.\nGuaranteed power of Parliament.",
                hook: "\"Glorious\" because the king fled rather than fought.",
            },
            {
                front: "Battle of the Boyne (1690) ⚔️",
                back: "William III defeated James II in Ireland. Still celebrated by some in Northern Ireland today.",
            },
            {
                front: "Elizabeth I ⚓",
                back: "The 'Golden Age'\nDefeated Spanish Armada (1588)",
            },
            {
                front: "The Civil War (1642) ⚔️",
                back: "Cavaliers (King) vs\nRoundheads (Parliament)\nCharles I executed 1649",
            },
            {
                front: "Lady Jane Grey 👑",
                back: "Reigned for 9 days in 1553, executed by Mary I",
            },
            {
                front: "The Union of the Crowns (1603) 👑",
                back: "James VI of Scotland became James I of England – united crowns",
            },
            {
                front: "Henry VIII's Wives 👑",
                back: "1. Catherine of Aragon\n2. Anne Boleyn\n3. Jane Seymour\n4. Anne of Cleves\n5. Catherine Howard\n6. Catherine Parr",
                hook: "Divorced, beheaded, died; divorced, beheaded, survived.",
            },
            {
                front: "North American Colonies ⚓",
                back: "English settlers began moving to North America during Queen Elizabeth I's reign",
            },
            {
                front: "Sir Francis Drake ⛵",
                back: "Elizabethan sailor who helped defeat Spanish Armada and sailed around the world",
            },
            {
                front: "Who was Oliver Cromwell? 🛡️",
                back: "Lord Protector (circa 1640s-1650s) who led Britain whilst it was without a monarch",
            },
            {
                front: "Henry VIII — why he matters ⛪",
                back: "Six wives.\nBroke from Rome and created the Church of England, making the monarch its head.\nThis is why England became Protestant.",
                hook: "Pope refused a divorce, so he made himself head of his own church.",
            },
        ],
    },
    {
        title: "🚂 5. Empire & Industry",
        cards: [
            {
                front: "Bill of Rights (1689) 📜",
                back: "Confirmed rights of Parliament and limits of king's power.\nParliament controlled who could be monarch.\nMonarch must be Protestant.",
                hook: "Straight after the Glorious Revolution (1688): the win gets written down the following year.",
            },
            {
                front: "Constitutional Monarchy 👑",
                back: "Monarch remained important but could no longer insist on policies without Parliament's agreement.",
            },
            {
                front: "Whigs and Tories 🏛️",
                back: "Two main groups in Parliament.\nBeginning of party politics.\nTories evolved into modern Conservative Party.",
            },
            {
                front: "Free Press (from 1695) 📰",
                back: "Newspapers allowed to operate without government licence. Increasing numbers of newspapers published.",
            },
            {
                front: "Act of Union (1707) 🤝",
                back: "United Kingdom of England and Scotland into 'Great Britain'.\nScotland kept own legal/education systems and Presbyterian Church.",
                hook: "1707 joins Scotland (Great Britain). Do not mix it with 1800, which joins Ireland.",
            },
            {
                front: "First Prime Minister 🏛️",
                back: "Sir Robert Walpole (1721-1742).\nFirst man called Prime Minister.\nLongest-serving PM in British history.",
                hook: "Walpole was first and still longest-serving — over 20 years.",
            },
            {
                front: "Jacobite Rebellions 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Attempts to put Stuart king back on throne. Bonnie Prince Charlie defeated at Battle of Culloden (1746).",
                hook: "Jacobite comes from Jacobus, Latin for James — they wanted the Stuart James line back. Ended at Culloden.",
            },
            {
                front: "Highland Clearances 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "After Culloden, landlords destroyed small farms (crofts) for sheep/cattle.\nMany Scots left for North America.",
            },
            {
                front: "Scottish Enlightenment 💡",
                back: "18th century thinkers: Adam Smith (economics), David Hume (philosophy), James Watt (steam power).",
            },
            {
                front: "Slave Trade ⛓️",
                back: "Slaves from West Africa taken to America/Caribbean on British ships.\nWilliam Wilberforce and Quakers led abolition.",
            },
            {
                front: "Abolition of Slavery 🕊️",
                back: "1807: Slave trade illegal in British ships.\n1833: Emancipation Act abolished slavery throughout British Empire.",
                hook: "Two dates: 1807 stops the trade, 1833 frees the people. Trade first, then freedom.",
            },
            {
                front: "American Independence (1776) 🇺🇸",
                back: "13 colonies declared independence. 'No taxation without representation'.\nBritain recognised independence in 1783.",
            },
            {
                front: "Battle of Trafalgar (1805) ⛵",
                back: "Admiral Nelson defeated Napoleon's combined French and Spanish fleets.\nNelson killed in battle.\nHMS Victory can be visited in Portsmouth.",
                hook: "Nelson wins at sea (1805); Wellington on land (1815).",
            },
            {
                front: "Battle of Waterloo (1815) ⚔️",
                back: "Duke of Wellington defeated Napoleon. Ended French Wars.",
                hook: "Waterloo ends Napoleon for good — ten years after Trafalgar.",
            },
            {
                front: "Act of Union (1800) 🇮🇪",
                back: "Ireland unified with England, Scotland and Wales. Created United Kingdom of Great Britain and Ireland.",
                hook: "1800 joins Ireland, creating the \"United Kingdom\". Scotland was already in from 1707.",
            },
            {
                front: "The Union Flag 🇬🇧",
                back: "Combines the crosses of St George (England), St Andrew (Scotland) and St Patrick (Ireland).",
                hook: "Three crosses, not four — Wales was already joined to England.",
                image: "/flags/union-flag.svg",
                imageAlt: "The Union Flag, combining the crosses of St George, St Andrew and St Patrick",
            },
            {
                front: "Victorian Age (1837-1901) 👑",
                back: "Queen Victoria reigned 64 years.\nBritish Empire grew to the largest in history (400M+ people).\nGrowing middle class and major social reforms.",
            },
            {
                front: "Crimean War (1853-1856) ⚔️",
                back: "Britain fought with Turkey and France against Russia.\nFirst war covered by media.\nFlorence Nightingale improved hospital conditions.",
                hook: "The war that gave us both Florence Nightingale and the Victoria Cross.",
            },
            {
                front: "Victoria Cross 🎖️",
                back: "Introduced by Queen Victoria during Crimean War. Honours acts of valour by soldiers.",
            },
            {
                front: "Irish Potato Famine (1840s) 🥔",
                back: "Potato crop failed.\n1 million died, 1.5 million left Ireland.\nMany came to Liverpool, London, Manchester, Glasgow.",
                hook: "1 million died, 1.5 million left — more emigrated than died.",
            },
            {
                front: "Reform Acts 🗳️",
                back: "1832: Increased voters, abolished pocket/rotten boroughs.\n1867: More urban seats, reduced property requirement.\n1884: Further extension.",
            },
            {
                front: "Suffragettes ✊",
                back: "Women campaigned for vote.\nEmmeline Pankhurst founded Women's Social and Political Union (WSPU) in 1903.\nUsed civil disobedience.",
                hook: "Suffrage means the right to vote. Pankhurst founded the WSPU.",
            },
            {
                front: "Isaac Newton 🍎",
                back: "Discovered Gravity\nPublished 'Principia'\nShowed gravity applies to the whole universe",
            },
            {
                front: "The Industrial Revolution 🏭",
                back: "Began in mid-18th century\nUK was major producer of coal, iron, cotton cloth",
            },
            {
                front: "The East India Company 🏢",
                back: "Traded with India, ruled Indian subcontinent until 1858",
            },
            {
                front: "The Chartists (1838-48) 📋",
                back: "Campaign for working-class votes and secret ballots",
            },
            {
                front: "Florence Nightingale 🏥",
                back: "Founder of modern nursing, famous for Crimean War work",
            },
            {
                front: "The Enlightenment (1700s) 💡",
                back: "New ideas about politics, philosophy & science.\nAdam Smith (economics) & David Hume (philosophy) were key Scottish thinkers.",
            },
            {
                front: "Who was Sir Robert Walpole? 🏛️",
                back: "First Prime Minister (1721-1742). Relied upon by George I (who spoke poor English)",
            },
            {
                front: "Reform Act (1832) 🗳️",
                back: "Abolished pocket/rotten boroughs, gave seats to towns/cities, increased male voters",
                hook: "\"Rotten boroughs\" were near-empty seats still electing MPs; 1832 swept them away.",
            },
            {
                front: "Emancipation Act (1833) 🕊️",
                back: "Abolished slavery.\nWilliam Wilberforce was leading abolitionist; Quakers set up first groups. >2M migrants came from India/China to replace labor.",
            },
            {
                front: "Isambard Kingdom Brunel 🚂",
                back: "Great Victorian engineer (designed bridges, trains, tunnels, and ships)",
            },
            {
                front: "Richard Arkwright 🏭",
                back: "Efficient and profitable factory owner during the Industrial Revolution",
            },
            {
                front: "Rudyard Kipling ✍️",
                back: "Indian-born author/poet whose work reflected the idea that the British Empire was a force for good",
            },
            {
                front: "George & Robert Stephenson 🚂",
                back: "Famous pioneers of railway engines",
            },
            {
                front: "Elizabethan period (1500s) 🎭",
                back: "Known for growing patriotism, expanded trade, and rich poetry/drama",
            },
        ],
    },
    {
        title: "🕊️ 6. 20th Century & Modern Britain",
        cards: [
            {
                front: "Pre-WWI Reforms 🏛️",
                back: "Financial help for unemployed, old-age pensions, free school meals, improved workplace safety, salary for MPs introduced.",
            },
            {
                front: "First World War (1914-1918) ⚔️",
                back: "Britain part of Allied Powers.\nOver 2 million British casualties.\nBattle of Somme: 60,000 casualties on first day.\nEnded 11 Nov 1918.",
            },
            {
                front: "Partition of Ireland (1922) 🇮🇪",
                back: "6 northern counties (mainly Protestant) remained in UK as Northern Ireland.\nRest became Irish Free State (later Republic in 1949).",
            },
            {
                front: "The Troubles (c.1969-1998) ☘️",
                back: "Conflict between those wanting full Irish independence and those wanting to remain loyal to British government. ~3,000 deaths.",
            },
            {
                front: "Inter-war Period (1920s-30s) 📻",
                back: "Improved housing.\nGreat Depression caused mass unemployment.\nBBC started radio (1922) and first regular TV service (1936).",
            },
            {
                front: "Second World War (1939-1945) ⚔️",
                back: "UK and France declared war after Hitler invaded Poland. Winston Churchill became PM in 1940.",
            },
            {
                front: "Battle of Britain (1940) ✈️",
                back: "RAF defeated German Luftwaffe using Spitfire and Hurricane planes. Prevented German invasion.",
                hook: "Fought in the air, not on the ground — Spitfires and Hurricanes stopped the invasion.",
            },
            {
                front: "The Blitz 💥",
                back: "German bombing of London and other British cities at night.\nCoventry almost destroyed. 'Blitz spirit' – national resistance.",
            },
            {
                front: "D-Day (6 June 1944) ⚓",
                back: "Allied forces landed in Normandy. Led to liberation of France and defeat of Germany in May 1945.",
                hook: "Normandy landings, June 1944; Germany surrenders less than a year later, May 1945.",
            },
            {
                front: "Welfare State 🏛️",
                back: "Labour government (1945) led by Clement Attlee introduced welfare state based on Beveridge Report.",
                hook: "Attlee won in 1945 and built the welfare state on Beveridge's 1942 blueprint.",
            },
            {
                front: "NHS Founded (1948) 🏥",
                back: "National Health Service established by Aneurin Bevan. Free healthcare at point of use.",
                hook: "Bevan built the NHS; Beveridge wrote the report. Two similar names, two different men.",
            },
            {
                front: "Post-War Migration 🛂",
                back: "Labour shortages led to recruitment from Ireland, Europe, West Indies, India, Pakistan, Bangladesh (1950s-60s).",
            },
            {
                front: "Swinging Sixties ☮️",
                back: "Growth in fashion, cinema, pop music (Beatles, Rolling Stones).\nLiberalised laws on divorce and abortion.\nEqual pay for women.",
            },
            {
                front: "UK Joins EEC (1973) 🇪🇺",
                back: "UK joined European Economic Community (later EU). First national referendum on membership in 1975.",
                hook: "In 1973, out 2020 — a 47-year membership.",
            },
            {
                front: "Margaret Thatcher (1979-1990) 👩‍💼",
                back: "Britain's first woman PM, known as the 'Iron Lady'.\nPrivatisation of nationalised industries.\nLegal controls on trade unions.\nFalklands War (1982).",
            },
            {
                front: "Good Friday Agreement (1998) ☘️",
                back: "Peace process for Northern Ireland.\nPower-sharing government established.\nNorthern Ireland Assembly created.",
                hook: "1998 peace and power-sharing; 1999 devolution.",
            },
            {
                front: "Devolution (1999) 🏛️",
                back: "Scottish Parliament and Welsh Assembly established. More control over local matters.",
                hook: "Powers devolve outward from Westminster in 1999 — Scotland gets a Parliament, Wales an Assembly.",
            },
            {
                front: "Brexit Referendum (2016) 🇪🇺",
                back: "UK voted 51.9% to 48.1% to leave European Union. UK formally left on 31 January 2020.",
                hook: "Voted 2016, left 2020 — nearly four years between the vote and the exit.",
            },
            {
                front: "Conflicts in Afghanistan & Iraq ⚔️",
                back: "UK forces involved in global fight against terrorism.\nCombat troops left Iraq in 2009.\nOperations in Afghanistan till 2021.",
            },
            {
                front: "Women's Suffrage 🗳️",
                back: "1918: Women 30+ vote\n1928: Equal voting age (21)\nfor men and women",
                hook: "1918 gets women over 30 the vote; 1928 levels it at 21 for everyone. Ten years apart.",
            },
            {
                front: "Winston Churchill 💂",
                back: "Prime Minister in 1940\nLed UK through WW2\nFamous for 'speech' morale",
            },
            {
                front: "The Battle of Britain (1940) ✈️",
                back: "RAF defeated Luftwaffe, prevented German invasion",
            },
            {
                front: "The Good Friday Agreement (1998) ☘️",
                back: "Peace deal for Northern Ireland – power-sharing government",
            },
            {
                front: "The Falklands War (1982) ⚓",
                back: "Argentina invaded Falklands; UK reclaimed territory",
            },
            {
                front: "Irish Home Rule (1913) 🇮🇪",
                back: "Proposed self-governing Ireland; postponed by WWI. Led to Easter Rising (1916)",
            },
            {
                front: "Splitting of Ireland (1921) 🇮🇪",
                back: "Peace treaty signed, splitting Ireland into two parts",
            },
            {
                front: "Battle of the Somme (1916) ⚔️",
                back: "WWI battle where British forces suffered 60,000 casualties on the first day",
                hook: "60,000 British casualties on the first day alone — the number the exam asks for.",
            },
            {
                front: "WWI Armistice 🕊️",
                back: "Ended at 11:00 on 11 November 1918",
            },
            {
                front: "Great Depression (1929) 📉",
                back: "High unemployment, but aviation/automobile industries developed",
            },
            {
                front: "Outbreak of WWII (1939) ⚔️",
                back: "German invasion of Poland led UK and France to declare war",
            },
            {
                front: "Dunkirk Evacuation (1940) ⚓",
                back: "Rescue of 300,000 men by volunteers and small boats",
                hook: "300,000 rescued by little boats — a retreat, not a victory, but a famous one.",
            },
            {
                front: "The Boer War (1889-1902) ⚔️",
                back: "Fought in South Africa",
            },
            {
                front: "Beveridge Report (1942) 📋",
                back: "William Beveridge set out ideas leading to the foundation of the modern welfare state",
                hook: "Beveridge wrote the plan (1942); Attlee's government built it after 1945.",
            },
            {
                front: "Education Act (1944) 🏫",
                back: "R A Butler introduced free secondary education and distinction between primary/secondary",
                hook: "The Butler Act — free secondary education for all, and the primary/secondary split.",
            },
            {
                front: "Independence of Colonies (1947) 🌐",
                back: "9 colonies gained independence, including India, Pakistan, and Sri Lanka",
            },
            {
                front: "The Liberal 1960s ☮️",
                back: "Increased wealth and liberalization of social laws (e.g. abortion and divorce)",
            },
            {
                front: "Clement Attlee 🏛️",
                back: "Churchill's Deputy PM; became PM in 1945. Nationalised major industries & created NHS",
            },
        ],
    },
    {
        title: "🏛️ 7. Government & Parliament",
        cards: [
            {
                front: "The UK Constitution 📖",
                back: "Unwritten\nBased on statutes,\nconventions & documents",
            },
            {
                front: "Minor Crimes in Scotland? ⚖️",
                back: "Justice of the Peace Court\n(Magistrates' equivalent)",
            },
            {
                front: "The Speaker 🎤",
                back: "Neutral MP who\nmanages debates in the\nHouse of Commons",
                hook: "The Speaker keeps order and drops all party loyalty — neutrality is the whole point of the job.",
            },
            {
                front: "What is the House of Lords?",
                back: "Reviews bills, proposes amendments; includes life peers and bishops",
            },
            {
                front: "Who is the head of state?",
                back: "The Monarch (currently King Charles III)",
            },
            {
                front: "Minimum age to become an MP",
                back: "18 years old",
            },
            {
                front: "What is a 'hung Parliament'?",
                back: "No party has majority; can form coalition or minority government",
                hook: "\"Hung\" because no single party can carry a vote alone — so they must share or govern in a minority.",
            },
            {
                front: "Small Claims limit in England/Wales",
                back: "Up to £10,000",
            },
            {
                front: "What is Hansard?",
                back: "The official written record of what is said in Parliament",
                hook: "Named after the printer who first published it — Hansard is the written record of Parliament.",
            },
            {
                front: "Jury Service eligibility",
                back: "You are eligible at 18; it is a duty of citizenship",
            },
            {
                front: "MOT Certificate requirement",
                back: "Required for vehicles more than 3 years old (not 2)",
            },
            {
                front: "'R' Plates in Northern Ireland",
                back: "New drivers must display an 'R' plate for 1 year",
            },
            {
                front: "3 key roles of School Governors",
                back: "Strategic direction, Accountability, Monitoring performance",
            },
            {
                front: "Examples of Criminal Offences",
                back: "Selling tobacco to under-18s, smoking in public, drinking in public",
            },
            {
                front: "Examples of Civil Offences",
                back: "Unfair dismissal, discrimination, faulty goods disputes",
            },
            {
                front: "How to register to vote",
                back: "You must register at your local council electoral registration office",
            },
            {
                front: "What are the three parts of Parliament?",
                back: "The Sovereign (King), House of Lords, House of Commons",
                hook: "Two Houses plus the Sovereign. People forget the monarch is formally part of Parliament.",
            },
            {
                front: "How many MPs in the House of Commons?",
                back: "650 (as of 2024)",
                hook: "650 MPs = 650 constituencies. One each.",
            },
            {
                front: "What is the role of the Prime Minister?",
                back: "Head of government, appoints ministers, leads Cabinet",
            },
            {
                front: "What is 'Question Time'?",
                back: "Hour where MPs question ministers on policies (Mon-Wed)",
            },
            {
                front: "What is the 'Official Opposition'?",
                back: "Largest party not in government; led by Leader of the Opposition",
            },
            {
                front: "How often are general elections?",
                back: "At least every 5 years (Fixed-term Parliaments Act)",
            },
            {
                front: "Voting age in the UK",
                back: "18 years old (16 in Scottish local elections)",
                hook: "18 everywhere, except 16 for Scottish local elections. The Scottish exception is the tested bit.",
            },
            {
                front: "What is 'First Past the Post'?",
                back: "Voting system for UK general elections – candidate with most votes wins constituency",
                hook: "Most votes in your constituency wins it — no prize for coming second.",
            },
            {
                front: "Who can stand as an MP?",
                back: "British/Commonwealth/Irish citizen aged 18+, not disqualified (e.g., undischarged bankrupt)",
            },
            {
                front: "What is a constituency?",
                back: "Geographical area represented by one MP",
            },
            {
                front: "Who runs the election count?",
                back: "Returning Officer (local council official)",
            },
            {
                front: "What is a council?",
                back: "Local authority responsible for schools, rubbish, housing, planning",
            },
            {
                front: "What is the 'Mayor of London'?",
                back: "Elected leader of Greater London Authority (since 2000)",
            },
            {
                front: "Which UK nations have devolved parliaments?",
                back: "Scotland (Scottish Parliament), Wales (Senedd), Northern Ireland (Assembly)",
            },
            {
                front: "What powers does the Scottish Parliament have?",
                back: "Education, health, justice, some tax powers",
            },
            {
                front: "Number of members in Scottish Parliament",
                back: "129",
            },
            {
                front: "Number of members in Welsh Senedd",
                back: "60",
            },
            {
                front: "Who is the current monarch?",
                back: "King Charles III (since 8 September 2022)",
            },
            {
                front: "What is the role of the monarch?",
                back: "Head of state, appoints PM, opens Parliament, royal assent to laws",
            },
            {
                front: "Who is the heir to the throne?",
                back: "Prince William, Duke of Cornwall and Cambridge (eldest son of Charles III)",
            },
            {
                front: "What is the 'Privy Council'?",
                back: "Advises the monarch; includes senior politicians and judges",
                hook: "\"Privy\" means private — a private advisory body to the monarch, nothing to do with local councils.",
            },
            {
                front: "The Queen's official birthday",
                back: "Celebrated in June with Trooping the Colour (actual birthday 21 April)",
                hook: "Two birthdays: the real one in April, the official parade in June when the weather is better.",
            },
        ],
    },
    {
        title: "⚖️ 8. Law & Justice",
        cards: [
            {
                front: "Criminal Law 🚨",
                back: "Relates to crimes investigated by police.\nExamples: carrying a weapon, drugs offences, racial crime.",
                hook: "Criminal = the state prosecutes. Civil = two parties dispute.",
            },
            {
                front: "Civil Law 📋",
                back: "Settles disputes between individuals/groups.\nExamples: housing law, consumer rights, employment law, debt.",
                hook: "No police, no prison — civil law settles disputes and awards remedies.",
            },
            {
                front: "Magistrates' Court ⚖️",
                back: "Deals with most minor criminal cases in England, Wales and NI.\nMagistrates are unpaid volunteers from local community.",
                hook: "Minor cases, unpaid volunteer magistrates, no jury. Serious cases go up to the Crown Court.",
            },
            {
                front: "Justice of the Peace Court 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Scottish equivalent of Magistrates' Court for minor criminal offences.",
            },
            {
                front: "Crown Court ⚖️",
                back: "Serious offences tried before judge and jury (12 members in England/Wales/NI, 15 in Scotland).",
            },
            {
                front: "Youth Court 👶",
                back: "For accused persons aged 10-17.\nUp to 3 specially trained magistrates.\nPublic not allowed.\nNames/photos cannot be published.",
            },
            {
                front: "Children's Hearings System 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Scottish system for dealing with children and young people who have committed offences.",
            },
            {
                front: "County Court 🏛️",
                back: "Deals with civil disputes: debt recovery, personal injury, family matters, breach of contract, divorce.",
            },
            {
                front: "Small Claims Procedure 💷",
                back: "Informal way to settle minor disputes without lawyer.\nClaims under £5,000 (England/Wales) or £3,000 (Scotland/NI).",
                hook: "Small claims are the DIY option: no lawyer needed, and the limit is lower in Scotland and NI.",
            },
            {
                front: "Police and Crime Commissioners 👮",
                back: "Directly elected individuals responsible for efficient and effective police force.\nSet local priorities and budget.\nAppoint Chief Constable.",
            },
            {
                front: "Terrorism and Extremism 🚫",
                back: "UK faces threats from Al Qa'ida, ISIL and Northern Ireland-related terrorism.\nReport concerns to local police.",
            },
            {
                front: "Solicitors 👨‍⚖️",
                back: "Trained lawyers who give legal advice, take action for clients, represent in court.\nCharges based on time spent on case.",
            },
            {
                front: "Citizens Advice Bureau 📋",
                back: "Can give names of local solicitors and areas of law they specialise in.",
            },
            {
                front: "Three Scottish Verdicts 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Guilty, not guilty, and 'not proven' are possible verdicts in Scottish courts.",
                hook: "Scotland has a third option: \"not proven\". England does not.",
            },
            {
                front: "What are the two types of law?",
                back: "Criminal law (punish offenders) and Civil law (disputes between individuals)",
            },
            {
                front: "Highest court in the UK",
                back: "Supreme Court (since 2009, replaced House of Lords)",
                hook: "The Supreme Court took over from the House of Lords in 2009 — courts and Parliament finally separated.",
            },
            {
                front: "What is a magistrate?",
                back: "Volunteer judge (no legal qualification) hears minor criminal cases",
            },
            {
                front: "Role of the police",
                back: "Maintain public order, prevent and investigate crime",
            },
            {
                front: "European Convention on Human Rights 📜",
                back: "UK helped draft & was one of first to sign (1950).\nPrinciples: right to life, fair trial, freedom of thought/religion/speech.",
                hook: "Human Rights Act 1998 brought it into UK law. Not the EU.",
            },
            {
                front: "Human Rights Act 1998 ⚖️",
                back: "Incorporated European Convention into UK law. Government, public bodies & courts must follow it.",
                hook: "The 1998 Act is how the 1950 Convention became enforceable in British courts.",
            },
            {
                front: "Protected Characteristics 🛡️",
                back: "Age, disability, sex, pregnancy/maternity, race, religion/belief, sexuality, marital status – all protected from discrimination.",
            },
            {
                front: "Domestic Violence Law ⚠️",
                back: "Serious crime in UK.\nViolence towards partner (married or not) can be prosecuted.\nMarital rape is a crime.",
            },
            {
                front: "Domestic Violence Helpline 📞",
                back: "24-hour National Domestic Violence Freephone Helpline: 0808 2000 247.",
                hook: "0808 2000 247 — free, 24 hours.",
            },
            {
                front: "Female Genital Mutilation (FGM) 🚫",
                back: "Illegal in UK. Practising FGM or taking girl/woman abroad for FGM is a criminal offence.",
            },
            {
                front: "Forced Marriage Law 💍",
                back: "Forcing someone to marry is a criminal offence.\nForced Marriage Protection Orders available since 2008 (England/Wales/NI).",
            },
            {
                front: "Arranged vs Forced Marriage",
                back: "Arranged marriage (both parties consent) is acceptable. Forced marriage (no consent) is illegal.",
                hook: "Consent is the whole difference: arranged is legal, forced is a crime.",
            },
            {
                front: "Equality Commission NI",
                back: "www.equalityni.org – handles discrimination issues in Northern Ireland.",
            },
            {
                front: "Equality and Human Rights Commission",
                back: "www.equalityhumanrights.com – handles discrimination in England & Wales.",
            },
        ],
    },
    {
        title: "🤝 9. Values & Citizenship",
        cards: [
            {
                front: "5 Fundamental British Values 🌟",
                back: "1. Democracy\n2. Rule of law\n3. Individual liberty\n4. Tolerance of different faiths and beliefs\n5. Participation in community life",
                hook: "\"Mutual respect\" is NOT one of the five.",
            },
            {
                front: "The Citizenship Pledge 📜",
                back: "'I will give my loyalty to the UK and respect its rights and freedoms.\nI will uphold its democratic values.\nI will observe its laws faithfully and fulfil my duties and obligations as a British citizen.'",
            },
            {
                front: "Responsibilities of Residents 🏡",
                back: "Respect and obey the law.\nRespect others' rights and opinions.\nTreat others with fairness.\nLook after yourself and your family.\nLook after your area and the environment.",
            },
            {
                front: "Freedoms the UK Offers 🗽",
                back: "Freedom of belief and religion.\nFreedom of speech.\nFreedom from unfair discrimination.\nRight to a fair trial.\nRight to vote in elections.",
            },
            {
                front: "No Place for Extremism 🚫",
                back: "There is no place in British society for extremism or intolerance.\nBritish society is founded on fundamental values protected by law, customs and expectations.",
            },
            {
                front: "Becoming a Permanent Resident 🛂",
                back: "You need to: 1) Speak and read English.\n2) Have a good understanding of life in the UK.\nPass the Life in the UK test OR pass an ESOL course with Citizenship.",
            },
            {
                front: "Life in the UK Test Details 📝",
                back: "24 questions in 45 minutes.\nMust score 75% (18/24) to pass.\nTaken at registered test centres.\nAvailable in English, Welsh or Scottish Gaelic.",
            },
            {
                front: "Book Your Test 📅",
                back: "Only at registered test centres (approx 60 centres).\nBook online at www.lifeintheuktest.gov.uk.\nNeed identification and proof of address.",
            },
            {
                front: "Taking the Oath of Allegiance 🤝",
                back: "New citizens make a pledge as part of the citizenship ceremony to uphold democratic values, observe laws faithfully, and fulfil duties as a British citizen.",
            },
            {
                front: "What is British Society Based On? 🏛️",
                back: "British society is founded on fundamental values and principles based on history and traditions, protected by law, customs and expectations.",
            },
            {
                front: "Responsibility to Vote 🗳️",
                back: "Voting in elections is both a right and a responsibility of citizenship.\nYou must register at your local council electoral registration office.",
            },
            {
                front: "Freedom of Speech vs Hate Speech 🗣️",
                back: "Freedom of speech is protected, but this does not give the right to express views that incite hatred or violence against others.",
                hook: "Free speech is protected right up to the point it incites hatred or violence.",
            },
            {
                front: "Mutual Respect Defined 🤲",
                back: "Respecting the rights of others, including their right to their own opinions, even when you disagree with them.",
            },
            {
                front: "Individual Liberty Meaning 🆓",
                back: "The freedom to live your life as you choose, as long as you do not break the law or infringe on the rights of others.",
            },
            {
                front: "How Laws Are Made to Protect Values ⚖️",
                back: "UK values are reflected in laws against discrimination, hate crime, and terrorism.\nThe Human Rights Act (1998) protects fundamental rights and freedoms.",
            },
            {
                front: "ESOL with Citizenship Course 📖",
                back: "If your English is below ESOL Entry Level 3, you must take this course.\nIt improves your English and teaches about life in the UK.",
            },
            {
                front: "What is the official religion?",
                back: "Christianity (Church of England is established in England)",
            },
            {
                front: "What does 'Rule of Law' mean?",
                back: "No one is above the law; laws apply equally to all",
            },
            {
                front: "Tolerance in the UK",
                back: "Respect for different religions, beliefs, and lifestyles",
            },
            {
                front: "Commonwealth: Can it suspend members?",
                back: "Yes, it can suspend membership but has no power over members",
            },
            {
                front: "EU Referendum date",
                back: "Held on 23 June 2016",
            },
            {
                front: "EU Exit date",
                back: "The UK formally left on 31 January 2020",
            },
            {
                front: "Recycling: Why important?",
                back: "Uses less energy than raw materials and reduces landfill",
            },
            {
                front: "Jury Service 👨‍⚖️",
                back: "Randomly selected from electoral register.\nAged 18-70.\nMust do it unless not eligible or good reason to be excused.",
                hook: "Picked at random from the electoral register, ages 18 to 70 — a duty, not a choice.",
            },
            {
                front: "Helping in Schools 🏫",
                back: "Parents can help in classrooms, support activities, listen to children read.\nPTAs organise fundraising events.",
            },
            {
                front: "School Governors 🏛️",
                back: "People from local community aged 18+.\nThree key roles: strategic direction, accountability, monitoring performance.",
            },
            {
                front: "Supporting Political Parties 🗳️",
                back: "Parties welcome new members. Canvassing involves handing out leaflets and knocking on doors.",
            },
            {
                front: "Blood Donation 🩸",
                back: "Register at: www.blood.co.uk (England/N Wales), www.welsh-blood.org.uk (Wales), www.scotblood.co.uk (Scotland), www.nibts.org (NI).",
            },
            {
                front: "Organ Donation 🫀",
                back: "Register at www.organdonation.nhs.uk. Living people can donate a kidney.",
            },
            {
                front: "Volunteering 🤲",
                back: "Working for good causes without payment. Benefits: meet people, practice English, develop work skills.",
            },
            {
                front: "National Citizen Service 🧑‍🤝‍🧑",
                back: "For 16-17 year olds. Outdoor activities, skills development, community projects.",
            },
            {
                front: "Looking After the Environment ♻️",
                back: "Recycle waste.\nShop locally.\nWalk/use public transport to reduce pollution.",
            },
            {
                front: "Being a Good Neighbour 🏘️",
                back: "Introduce yourself.\nRespect privacy.\nLimit noise.\nKeep garden tidy.\nPut bins out on collection day.",
            },
            {
                front: "Citizenship Values 🏅",
                back: "Obey law, respect others' rights, treat with fairness, help family, protect environment, treat everyone equally, vote.",
            },
            {
                front: "Life in the UK Test required for?",
                back: "Indefinite leave to remain or naturalisation as British citizen",
            },
            {
                front: "Passing score for Life in the UK test",
                back: "75% (18 out of 24 questions correct)",
            },
            {
                front: "What is the 'Indefinite Leave to Remain' (ILR)?",
                back: "Permanent residence, allows work without visa restrictions",
            },
            {
                front: "How long to qualify for citizenship?",
                back: "Usually 5 years residence + 12 months with ILR",
                hook: "Usually 5 years in the UK, then 12 more months holding ILR.",
            },
        ],
    },
    {
        title: "🎊 10. Culture & Traditions",
        cards: [
            {
                front: "Christmas Day (25 Dec) 🎄",
                back: "Public holiday.\nTraditional meal: roast turkey, Christmas pudding, mince pies.\nGift-giving, cards, decorated trees.",
            },
            {
                front: "Boxing Day (26 Dec) 📦",
                back: "Public holiday.\nTraditionally tradesmen collected 'Christmas boxes' (money/gifts) as thanks for service.\nNow associated with football and shopping.",
            },
            {
                front: "Easter Traditions 🥚",
                back: "Good Friday & Easter Monday are public holidays.\nLent (40 days before Easter) begins on Ash Wednesday.\nShrove Tuesday = Pancake Day.",
            },
            {
                front: "Diwali 🪔",
                back: "Festival of Lights (Oct/Nov).\nCelebrated by Hindus & Sikhs.\nVictory of good over evil.\nFamous celebration in Leicester.",
                hook: "The festival of lights: good defeats evil. Hindus and Sikhs; Leicester holds a famous one.",
            },
            {
                front: "Hannukah 🕎",
                back: "Jewish festival (Nov/Dec).\n8 days.\nCandle-lighting on menorah.\nRemembers struggle for religious freedom.",
            },
            {
                front: "Eid al-Fitr 🌙",
                back: "End of Ramadan.\nMuslims thank Allah for strength to complete the fast.\nSpecial services and meals.",
                hook: "Fitr = breaking the fast, so this is the one that ends Ramadan. Eid ul Adha is the sacrifice one.",
            },
            {
                front: "Eid ul Adha 🐑",
                back: "Remembers Prophet Ibrahim's willingness to sacrifice his son. Animals sacrificed in slaughterhouses.",
                hook: "Adha = sacrifice, recalling Ibrahim. The other Eid (al-Fitr) ends Ramadan.",
            },
            {
                front: "Vaisakhi (Baisakhi) 🌾",
                back: "Sikh festival (14 April).\nCelebrates founding of the Khalsa community.\nParades, dancing, singing.",
            },
            {
                front: "Hogmanay (Scottish New Year) 🎆",
                back: "31 Dec is Hogmanay in Scotland.\n2 Jan is also a public holiday.\nBigger than Christmas for some Scots.",
            },
            {
                front: "Halloween (31 Oct) 🎃",
                back: "Ancient pagan festival marking start of winter. 'Trick or treat', pumpkin lanterns.",
            },
            {
                front: "Bonfire Night (5 Nov) 🔥",
                back: "Fireworks & bonfires. Marks failed Gunpowder Plot (1605) when Guy Fawkes tried to blow up Parliament.",
                hook: "Burning Guy Fawkes in effigy, 400+ years after the 1605 plot failed.",
            },
            {
                front: "Remembrance Day (11 Nov) 🌹",
                back: "Commemorates war dead.\nPoppies worn.\n2-minute silence at 11am.\nWreaths laid at Cenotaph, Whitehall.",
                hook: "11am on the 11th of the 11th — the moment WWI stopped in 1918.",
            },
            {
                front: "Bank Holidays in UK 📅",
                back: "Early May, late May/early June, August. NI also has Battle of the Boyne anniversary (July).",
            },
            {
                front: "TV Licence rules 📺",
                back: "Required for watching live TV.\nFree for over-75s.\n50% discount for blind people.\nFine up to £1,000.",
                hook: "Needed for live TV, free at 75+, half price if blind.",
            },
            {
                front: "Hogmanay 🎆",
                back: "Scottish New Year's Eve\nBigger than Christmas\nin Scotland traditionally",
            },
            {
                front: "Remembrance Day 🌹",
                back: "Nov 11th at 11am\nCommemorates WW1 end\n2-minute silence",
            },
            {
                front: "Bonfire Night (Guy Fawkes Night)",
                back: "Nov 5th – fireworks & bonfires to mark failed 1605 plot",
            },
            {
                front: "What is 'Shrove Tuesday'?",
                back: "Pancake Day, day before Lent starts",
                hook: "Pancakes use up eggs and fat the day before Lent's fasting begins.",
            },
            {
                front: "Lent",
                back: "Begins on Ash Wednesday (40 days before Easter)",
            },
            {
                front: "Flowers & Nations 🌼",
                back: "ENG: Rose\nSCO: Thistle\nWAL: Daffodil\nNI: Shamrock",
            },
            {
                front: "National Anthem of the UK",
                back: "'God Save the King'",
            },
            {
                front: "Official animal of Scotland",
                back: "The Unicorn (symbol of purity and power)",
            },
            {
                front: "Prince William's current title",
                back: "The Prince of Wales",
            },
            {
                front: "Church of England (Anglican) 🏛️",
                back: "Established (official) Church in England.\nMonarch is Supreme Governor.\nArchbishop of Canterbury is spiritual leader.",
                hook: "The established church in England only — the monarch is its Supreme Governor. Wales and NI have none.",
            },
            {
                front: "Church of Scotland (Presbyterian) 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "National Church in Scotland.\nPresbyterian governance (ministers & elders).\nModerator chairs General Assembly (1-year term).",
                hook: "Scotland's national church is Presbyterian, run by ministers and elders — not bishops.",
            },
            {
                front: "No established Church in Wales/NI",
                back: "Wales and Northern Ireland have no official state Church.",
            },
            {
                front: "Other Christian groups in UK",
                back: "Baptists, Methodists, Presbyterians, Quakers, Roman Catholic.",
            },
            {
                front: "UK Religion Census 2021 📊",
                back: "Christian 46.2%\nMuslim 6.5%\nHindu 1.7%\nSikh 1%\nJewish/Buddhist <0.5%\nNo religion 37.2%",
            },
            {
                front: "Patron Saints & Dates 📅",
                back: "1 Mar: St David (Wales)\n17 Mar: St Patrick (NI)\n23 Apr: St George (England)\n30 Nov: St Andrew (Scotland)",
                hook: "In calendar order: David (1 Mar), Patrick (17 Mar), George (23 Apr), Andrew (30 Nov).",
            },
            {
                front: "St David 🏴󠁧󠁢󠁷󠁬󠁳󠁿",
                back: "Patron saint of Wales. Day: 1 March. Symbol: Daffodil.",
                hook: "David = Daffodil = Wales. Both start with D; 1 March.",
            },
            {
                front: "St Patrick ☘️",
                back: "Patron saint of Ireland.\nDay: 17 March.\nSymbol: Shamrock.\nOfficial holiday in NI.",
                image: "/flags/st-patrick.svg",
                imageAlt: "St Patrick's Cross: red diagonal cross on white",
            },
            {
                front: "St George 🏴󠁧󠁢󠁥󠁮󠁧󠁿",
                back: "Patron saint of England.\nDay: 23 April.\nSymbol: Red Cross on white (St George's Cross).",
                hook: "The St George's Cross is the red cross on the Union Flag.",
                image: "/flags/st-george.svg",
                imageAlt: "St George's Cross: red cross on white",
            },
            {
                front: "St Andrew 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Patron saint of Scotland.\nDay: 30 November.\nSymbol: Saltire (white diagonal cross on blue).",
                hook: "Andrew = Scotland, 30 November, and the last of the four in the year.",
                image: "/flags/st-andrew.svg",
                imageAlt: "The Saltire: white diagonal cross on blue",
            },
            {
                front: "Religious freedom in the UK",
                back: "Everyone has the legal right to choose their religion, or to choose not to practise a religion.",
            },
            {
                front: "Where was football (soccer) codified?",
                back: "England (Football Association founded 1863)",
            },
            {
                front: "The Wimbledon Championships 🎾",
                back: "Oldest tennis tournament, held in London since 1877",
            },
            {
                front: "Who is Sir Andy Murray?",
                back: "Scottish tennis player, won Wimbledon 2013 & 2016",
            },
            {
                front: "The Ashes 🏏",
                back: "Cricket series between England and Australia",
            },
            {
                front: "The London Marathon 🏃",
                back: "Annual race since 1981, famous charity event",
            },
            {
                front: "Sir Steve Redgrave",
                back: "Won 5 consecutive gold medals in Rowing",
            },
            {
                front: "Dame Kelly Holmes",
                back: "Won 2 gold medals in Running (2004)",
            },
            {
                front: "Rugby origins",
                back: "Originated in England in the early 19th century",
            },
            {
                front: "Formula 1 stars from the UK",
                back: "Lewis Hamilton, Jenson Button, Damon Hill",
            },
            {
                front: "Dame Mary Peters 🏅",
                back: "Olympic athlete who promoted sport and tourism in Northern Ireland",
            },
            {
                front: "Traditional English Food 🥘",
                back: "Roast beef with Yorkshire puddings. Fish and chips.",
            },
            {
                front: "Traditional Welsh Food 🏴󠁧󠁢󠁷󠁬󠁳󠁿",
                back: "Welsh cakes – made from flour, dried fruits and spices. Served hot or cold.",
            },
            {
                front: "Traditional Scottish Food 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Haggis – sheep's stomach stuffed with offal, suet, onions and oatmeal.",
            },
            {
                front: "Traditional Northern Irish Food ☘️",
                back: "Ulster fry – bacon, eggs, sausage, black/white pudding, tomatoes, mushrooms, soda bread, potato bread.",
            },
            {
                front: "Pubs & Nightclubs 🍺",
                back: "18+ to buy alcohol.\n16+ can have wine/beer with meal if with someone 18+.\nPub quizzes, pool, darts.",
            },
            {
                front: "Betting & Gambling 🎰",
                back: "18+ to enter betting shops/casinos.\nNational Lottery draws weekly.\n£14.5bn spent on gambling (2017-18).",
            },
            {
                front: "Gardening in the UK 🌻",
                back: "Many have gardens.\nAllotments for growing fruit/veg.\nFamous gardens: Kew, Sissinghurst, Bodnant Garden.",
            },
            {
                front: "National Flowers by Nation 🌼",
                back: "England: Rose\nScotland: Thistle\nWales: Daffodil\nNorthern Ireland: Shamrock",
            },
            {
                front: "Pets in the UK 🐾",
                back: "Dogs must wear collar with owner's name/address. Owner responsible for control and cleaning up.",
            },
            {
                front: "Shopping in the UK 🛍️",
                back: "Most shops open 7 days.\nMarkets on certain days.\nMajor centres: Metrocentre, Trafford Centre, Westfield, Bluewater.",
            },
        ],
    },
    {
        title: "🎭 11. Arts, Science & Invention",
        cards: [
            {
                front: "Henry Purcell (1659-95) 🎵",
                back: "Organist at Westminster Abbey.\nWrote church music, operas.\nDeveloped distinctive British style.",
            },
            {
                front: "George Frederick Handel (1685-1759) 🎵",
                back: "German-born, became British citizen. Wrote Water Music, Music for Royal Fireworks, Messiah (oratorio).",
            },
            {
                front: "Gustav Holst (1874-1934) 🎵",
                back: "Wrote The Planets suite. Adapted Jupiter as tune for 'I Vow to Thee My Country'.",
            },
            {
                front: "Sir Edward Elgar (1857-1934) 🎵",
                back: "Best known for Pomp and Circumstance Marches. Land of Hope and Glory played at Last Night of the Proms.",
            },
            {
                front: "Ralph Vaughan Williams (1872-1958) 🎵",
                back: "Wrote for orchestras and choirs. Influenced by traditional English folk music.",
            },
            {
                front: "Benjamin Britten (1913-76) 🎵",
                back: "Best known for operas Peter Grimes and Billy Budd. Founded Aldeburgh Festival.",
            },
            {
                front: "British Art: Thomas Gainsborough 🎨",
                back: "Portrait painter (1727-88). Often painted people in country or garden scenery.",
            },
            {
                front: "J.M.W. Turner (1775-1851) 🎨",
                back: "Influential landscape painter.\nTurner Prize named after him.\nRaised profile of landscape painting.",
                hook: "The Turner Prize is named after him — landscapes, and a prize for modern art.",
            },
            {
                front: "John Constable (1776-1837) 🎨",
                back: "Landscape painter famous for works of Dedham Vale (Suffolk-Essex border).",
            },
            {
                front: "Pre-Raphaelites 🎨",
                back: "Important 19th century group.\nPainted detailed religious/literary scenes in bright colours.\nIncluded Holman Hunt, Rossetti, Millais.",
            },
            {
                front: "Henry Moore (1898-1986) 🎨",
                back: "English sculptor. Best known for large bronze abstract sculptures.",
            },
            {
                front: "Lucian Freud (1922-2011) 🎨",
                back: "German-born British artist. Best known for portraits.",
            },
            {
                front: "David Hockney (1937-) 🎨",
                back: "Important contributor to 'pop art' movement of 1960s. Continues to be influential.",
            },
            {
                front: "British Architecture 🏛️",
                back: "Inigo Jones (17th C), Sir Christopher Wren (St Paul's), Robert Adam (18th C), Sir Edwin Lutyens (Cenotaph), Sir Norman Foster (modern).",
            },
            {
                front: "Capability Brown 🌳",
                back: "18th century landscape gardener. Designed grounds to appear natural with grass, trees and lakes.",
            },
            {
                front: "Chelsea Flower Show 🌸",
                back: "Annual garden design showcase. Features British and international garden design.",
            },
            {
                front: "British Fashion Designers 👗",
                back: "Mary Quant, Alexander McQueen, Vivienne Westwood. London Fashion Week is one of 'Big Four' fashion weeks.",
            },
            {
                front: "Notable British Poets ✍️",
                back: "William Blake, John Keats, Lord Byron, Percy Shelley, Alfred Lord Tennyson, William Wordsworth, Wilfred Owen, John Betjeman, Ted Hughes.",
            },
            {
                front: "Poet's Corner 📖",
                back: "Located in Westminster Abbey. Many famous poets buried or commemorated there.",
            },
            {
                front: "George Orwell ✍️",
                back: "Wrote Animal Farm and Nineteen Eighty-Four. Satirical and political novels.",
            },
            {
                front: "Agatha Christie 🔍",
                back: "Famous detective story writer.\nCreated Hercule Poirot and Miss Marple.\nThe Mousetrap is longest-running West End show.",
            },
            {
                front: "J.R.R. Tolkien 📚",
                back: "Wrote The Lord of the Rings and The Hobbit. Voted UK's best-loved novel in 2003.",
            },
            {
                front: "British Cinema 🎬",
                back: "Charlie Chaplin, Alfred Hitchcock, David Lean.\nHarry Potter and James Bond franchises produced in UK.\nBAFTA awards.",
            },
            {
                front: "Monty Python 🎭",
                back: "Revolutionary comedy group.\nMonty Python's Flying Circus (1969) introduced new type of progressive comedy.",
            },
            {
                front: "Television 📺",
                back: "John Logie Baird (Scotsman, 1920s). First TV broadcast London-Glasgow (1932).",
            },
            {
                front: "Radar 📡",
                back: "Sir Robert Watson-Watt (Scotsman, 1935). Detected aircraft by radio waves.",
                hook: "Watson-Watt's radar (1935) is what made the Battle of Britain winnable five years later.",
            },
            {
                front: "Jet Engine ✈️",
                back: "Sir Frank Whittle (RAF engineer, 1930s).",
            },
            {
                front: "World Wide Web 🌐",
                back: "Sir Tim Berners-Lee (1990). First successful data transfer 25 Dec 1990.",
                hook: "Berners-Lee invented the Web, not the internet — and the first transfer was Christmas Day 1990.",
            },
            {
                front: "Penicillin 💊",
                back: "Discovered by Alexander Fleming (1928). Developed into usable drug by Florey & Chain.",
                hook: "Fleming found it by accident in 1928; Florey and Chain turned it into a usable drug.",
            },
            {
                front: "DNA Structure 🧬",
                back: "Discovered 1953 at British universities (London & Cambridge). Francis Crick was British.",
            },
            {
                front: "Hovercraft 🚤",
                back: "Sir Christopher Cockerell (1950s).",
            },
            {
                front: "ATM (Cashpoint) 🏧",
                back: "James Goodfellow (1960s). First used by Barclays Bank, Enfield (1967).",
                hook: "First cash machine: Barclays in Enfield, 1967.",
            },
            {
                front: "IVF Therapy 👶",
                back: "Pioneered by Sir Robert Edwards & Patrick Steptoe. First 'test-tube baby' born 1978 in Oldham.",
            },
            {
                front: "Cloning (Dolly the Sheep) 🐑",
                back: "Sir Ian Wilmot & Keith Campbell (1996). First cloned mammal.",
            },
            {
                front: "MRI Scanner 🏥",
                back: "Sir Peter Mansfield co-invented MRI, revolutionising diagnostic medicine.",
            },
            {
                front: "Concorde ✈️",
                back: "World's only supersonic passenger aircraft.\nDeveloped with France.\nFirst flew 1969, retired 2003.",
                hook: "Anglo-French, supersonic, first flew 1969 — the only one of its kind.",
            },
            {
                front: "Harrier Jump Jet ✈️",
                back: "Vertical take-off aircraft designed and developed in the UK.",
            },
            {
                front: "Insulin 💉",
                back: "John MacLeod (Scottish physician) co-discovered insulin for treating diabetes.",
            },
            {
                front: "Jodrell Bank Radio Telescope 📡",
                back: "Sir Bernard Lovell built it in Cheshire. For many years the world's largest.",
            },
            {
                front: "Turing Machine 💻",
                back: "Alan Turing (1930s). Theoretical mathematical device influential in computer science.",
                hook: "The Turing Machine was a theory, not a device — the idea underneath modern computing.",
            },
            {
                front: "The Proms 🎻",
                back: "8-week summer concert\nseries at Royal Albert Hall\nMainly classical music",
            },
            {
                front: "Alexander Fleming 💊",
                back: "Scottish scientist\nDiscovered Penicillin (1928)",
            },
            {
                front: "The Turner Prize 🎨",
                back: "Established in 1984\nCelebrates contemporary art",
            },
            {
                front: "William Shakespeare ✍️",
                back: "Playwright, 'Hamlet', 'Romeo and Juliet', 'Macbeth'",
            },
            {
                front: "Charles Darwin 🌿",
                back: "Theory of evolution, 'On the Origin of Species' (1859)",
            },
            {
                front: "The Beatles 🎸",
                back: "Rock band from Liverpool (1960s), global music icons",
            },
            {
                front: "George Frideric Handel",
                back: "Composed Water Music for George I and Royal Fireworks for George II",
            },
            {
                front: "Tim Berners-Lee",
                back: "Invented the World Wide Web",
            },
            {
                front: "Alan Turing",
                back: "Invented the Turing Machine – a major British innovation",
            },
            {
                front: "Sake Dean Mahomet",
                back: "Introduced the art of shampooing (head massage) to Britain",
            },
            {
                front: "The Bayeux Tapestry",
                back: "Commemorates the Battle of Hastings (1066) and is located in France",
            },
            {
                front: "British 20th Century Inventions 💡",
                back: "Jet engine, radar, TV, WWW, hovercraft, penicillin discovery, ATM, cloning (Dolly), supersonic Concorde, insulin, MRI, DNA structure, Jodrell Bank radio telescope, Harrier jump jet, IVF therapy",
            },
            {
                front: "Dylan Thomas ✍️",
                back: "Welsh poet; wrote 'Under Milk Wood' & 'Do Not Go Gentle into that Good Night'",
            },
            {
                front: "Roald Dahl 📚",
                back: "Welsh author of 'Charlie and the Chocolate Factory' & 'George's Marvellous Medicine'",
            },
            {
                front: "Sir Arthur Conan Doyle 🔍",
                back: "Scottish author who created 'Sherlock Holmes'",
            },
            {
                front: "Jane Austen 📚",
                back: "Novels: 'Pride and Prejudice', 'Sense and Sensibility'",
            },
            {
                front: "Charles Dickens 🎩",
                back: "'Oliver Twist', 'Great Expectations', highlighted social inequality",
            },
            {
                front: "Robert Burns 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Scottish poet (1759-96), known as 'The Bard'.\nWrote 'Auld Lang Syne'.\nCelebrated on Burns Night, 25 January.",
                hook: "Scotland's \"Bard\" wrote Auld Lang Syne — which is why it is sung at New Year.",
            },
            {
                front: "J.K. Rowling ⚡",
                back: "'Harry Potter' series, one of best-selling authors",
            },
            {
                front: "Who wrote 'The Hobbit'?",
                back: "J.R.R. Tolkien (Oxford academic)",
            },
        ],
    },
    {
        title: "🏡 12. Everyday Life & Places",
        cards: [
            {
                front: "Big Ben 🕰️",
                back: "Nickname for the great bell at Houses of Parliament.\nClock tower named 'Elizabeth Tower' (2012).\nOver 150 years old.",
                hook: "Big Ben is the bell, not the tower. The tower was renamed Elizabeth Tower in 2012.",
            },
            {
                front: "The Eden Project 🌿",
                back: "Cornwall.\nGiant greenhouses (biomes) housing plants from worldwide.\nEnvironmental charity.",
            },
            {
                front: "Edinburgh Castle 🏴󠁧󠁢󠁳󠁣󠁴󠁿",
                back: "Historic fortress on Castle Rock.\nRoyal castle since 12th century.\n26 sieges in 1,100-year history.",
            },
            {
                front: "Giant's Causeway ",
                back: "NE coast of Northern Ireland.\n50 million-year-old volcanic columns.\nHexagonal shapes.\nUNESCO World Heritage Site.",
            },
            {
                front: "Loch Lomond & Trossachs 🏞️",
                back: "National Park in west Scotland.\n720 sq miles.\nLargest freshwater expanse in mainland Britain.",
            },
            {
                front: "London Eye 🎡",
                back: "Cantilevered observation wheel on South Bank.\n135m tall.\nBuilt for Millennium celebrations.\n3.75m visitors/year.",
            },
            {
                front: "Snowdonia (Eryri) ⛰️",
                back: "National Park in North Wales.\n823 sq miles.\nSnowdon is highest mountain in Wales.\nDesignated 1951.",
                hook: "Snowdon is the highest mountain in Wales — Ben Nevis is the highest in the UK.",
            },
            {
                front: "Tower of London 🏰",
                back: "Built by William the Conqueror (1066).\nHome of Crown Jewels.\nYeoman Warders (Beefeaters) give tours.",
            },
            {
                front: "Lake District 🏞️",
                back: "England's largest national park (885 sq miles).\nFamous for lakes & mountains.\nWindermere is largest lake.",
            },
            {
                front: "National Trust 🏛️",
                back: "Charity preserving important buildings, coastline & countryside.\nFounded 1895.\n61,000+ volunteers.",
            },
            {
                front: "What is the national currency?",
                back: "Pound sterling (£), divided into 100 pence",
            },
            {
                front: "Typical TV licence fee (colour TV)",
                back: "£159 per year (funds the BBC, no ads)",
            },
            {
                front: "What is the NHS?",
                back: "National Health Service – publicly funded healthcare, founded 1948",
            },
            {
                front: "What is the driving age?",
                back: "17 (16 for mopeds in some areas)",
            },
            {
                front: "Millennium Projects",
                back: "The O2 and the London Eye were both Millennium landmarks",
            },
            {
                front: "Income Tax 💷",
                back: "Paid on wages, self-employment profits, pensions, savings, dividends.\nFunds roads, education, police, armed forces.",
            },
            {
                front: "PAYE System 📋",
                back: "Pay As You Earn – tax automatically deducted from employment income by employer and paid to HMRC.",
                hook: "Pay As You Earn — your employer sends the tax to HMRC before you see the money.",
            },
            {
                front: "Self-Assessment 📝",
                back: "Self-employed people complete a tax return and pay their own tax to HMRC.",
            },
            {
                front: "National Insurance (NI) 🛡️",
                back: "Paid by employed & self-employed. Funds state pension, NHS, and benefits like Jobseeker's Allowance.",
                hook: "NI buys your state pension and NHS; Income Tax funds general spending. Two separate deductions.",
            },
            {
                front: "NI Number 🔢",
                back: "Unique personal account number.\nSent to all young people before 16th birthday.\nNeeded for work.",
            },
            {
                front: "Driving Licence 🚗",
                back: "Minimum age: 17 (car/motorcycle), 16 (moped).\nMust pass driving test.\nValid until 70, then 3-year renewals.",
                hook: "17 for a car, 16 for a moped, and renewal every 3 years once you turn 70.",
            },
            {
                front: "MOT Test 🔧",
                back: "Required annually for vehicles over 3 years old. Tests roadworthiness.",
                hook: "Cars over 3 years old, tested every year. The \"3\" is the tested number.",
            },
            {
                front: "Vehicle Tax & Insurance 🚙",
                back: "Must pay annual road tax (display tax disc).\nMust have valid motor insurance – serious offence to drive without.",
            },
            {
                front: "R Plates (NI) 🅿️",
                back: "Newly qualified drivers in NI must display 'R' plate for 1 year after passing test.",
                hook: "R for Restricted — Northern Ireland only, for one year after passing.",
            },
            {
                front: "1066",
                back: "Battle of Hastings (William the Conqueror)",
            },
            {
                front: "1215",
                back: "Magna Carta – established that even the King is subject to the law",
            },
            {
                front: "1588",
                back: "Defeat of the Spanish Armada (Elizabeth I)",
            },
            {
                front: "1688",
                back: "The Glorious Revolution – guaranteed the power of Parliament over the King",
            },
            {
                front: "1805",
                back: "Battle of Trafalgar (Admiral Nelson died; statue in Trafalgar Square)",
            },
            {
                front: "1815",
                back: "Battle of Waterloo (Duke of Wellington defeated Napoleon)",
            },
            {
                front: "1918 & 1928",
                back: "1918: Women over 30 got the vote\n1928: Women got equal voting rights at 21",
                hook: "1918: women over 30. 1928: equal at 21. A decade apart.",
            },
            {
                front: "National Trust founded",
                back: "1895",
            },
            {
                front: "When did the UK join the EU?",
                back: "1973 (then EEC) – left on 31 Jan 2020 (Brexit)",
            },
            {
                front: "UK's permanent seat at?",
                back: "UN Security Council (one of five veto powers)",
            },
            {
                front: "What is NATO?",
                back: "North Atlantic Treaty Organization – military alliance, UK founding member 1949",
            },
            {
                front: "The 'Special Relationship'",
                back: "Close political & military ties between UK and USA",
            },
            {
                front: "Overseas Territories vs Crown Dependencies ⚖️",
                back: "Overseas Territories (like St Helena/Falklands) were colonies, whereas Crown Dependencies (like Channel Islands/Isle of Man) were not.",
                hook: "Territories were colonies; Dependencies never were.",
            },
            {
                front: "The Commonwealth 🌍",
                back: "Association of 56 countries supporting democracy and development.\nMost were once part of British Empire.\nKing is ceremonial head.\nVoluntary membership.",
            },
            {
                front: "Council of Europe 🏛️",
                back: "47 member countries.\nSeparate from EU.\nProtects human rights.\nCreated European Convention on Human Rights.",
                hook: "Not the EU: 47 countries, human rights only.",
            },
            {
                front: "European Court of Human Rights ⚖️",
                back: "Enforces European Convention on Human Rights. Based in Strasbourg, France.",
            },
            {
                front: "United Nations (UN) 🌐",
                back: "International organisation with 190+ members.\nAims to prevent war and promote peace.\nUK is one of 5 permanent UN Security Council members.",
                hook: "One of five permanent members — the five with a veto.",
            },
            {
                front: "NATO 🛡️",
                back: "North Atlantic Treaty Organization.\nMilitary alliance of European and North American countries.\nUK founding member (1949).",
            },
            {
                front: "G7 🌍",
                back: "Group of seven major advanced economies. UK is a member.",
            },
            {
                front: "UK and the EU 🇪🇺",
                back: "Joined EEC in 1973.\nVoted to leave in 2016 referendum.\nFormally left 31 January 2020.\nUK does not use Euro currency.",
            },
            {
                front: "British Overseas Territories 🏝️",
                back: "Connected to UK but not part of it.\nExamples: Gibraltar, Bermuda, Falkland Islands, St Helena, Cayman Islands.",
            },
            {
                front: "Crown Dependencies 👑",
                back: "Channel Islands (Jersey, Guernsey) and Isle of Man.\nOwn governments and legal systems.\nNot part of UK.\nUK responsible for defence.",
            },
        ],
    },
];
